(function (global, factory) {
	typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
	typeof define === 'function' && define.amd ? define('@getgo/auth-client',['exports'], factory) :
	(factory((global.getgoAuthClient = {})));
}(this, (function (exports) { 'use strict';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */



var __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
};

var assert = function (options, requiredKeys) {
    requiredKeys.forEach(function (key) {
        if (!options[key]) {
            throw new TypeError("Required \"" + key + "\" not defined");
        }
    });
};

var redirect = function (url) {
    location.assign(url);
    return new Promise(function () { return undefined; });
};

var keyValuePattern = /[^?&=]+=[^?&=]*/;
function stringify(obj) {
    if (typeof obj !== 'object') {
        return obj;
    }
    var str = [];
    Object.keys(obj || {}).forEach(function (p) {
        if (obj[p] !== '' && obj[p] !== undefined) {
            var key = encodeURIComponent(p);
            var value = encodeURIComponent(stringify(obj[p]));
            str.push(key + "=" + value);
        }
    });
    return str.join('&');
}
function parse(input) {
    var str = input;
    if (['?', '#'].indexOf(str.substr(0, 1)) !== -1) {
        str = str.substr(1);
    }
    return str.split('&').reduce(function (obj, set) {
        var _a = set.split('=').map(decodeURIComponent), key = _a[0], value = _a[1];
        var parsed;
        if (keyValuePattern.test(value)) {
            parsed = parse(value);
        }
        else {
            parsed = value;
        }
        obj[key] = parsed;
        return obj;
    }, {});
}

var login = function (options) {
    assert(options, ['client_id', 'url', 'redirect_url']);
    var params = {
        client_id: options.client_id,
        gateway: options.gateway,
        login_theme: options.theme,
        redirect_uri: options.redirect_url,
        response_type: 'token',
        scope: options.scope,
        state: options.state,
    };
    var url = options.url + "/oauth/authorize?" + stringify(params);
    var fn = options.onLoginRedirect || redirect;
    return fn(url);
};
var loginWithSSO = function (options) { return login(__assign({}, options, { gateway: true })); };

var logout = function (options) {
    assert(options, ['client_id', 'url', 'redirect_url']);
    var params = {
        client_id: options.client_id,
        redirect_uri: options.redirect_url,
    };
    var url = options.url + "/oauth/logout?" + stringify(params);
    var fn = options.onLogoutRedirect || redirect;
    return fn(url);
};

var tokenProps = [
    'token_type',
    'access_token',
    'expires',
    'expires_in',
    'issued',
    'scope',
    'state',
];
var Token = (function () {
    function Token(tokenish) {
        var _this = this;
        this.token_type = 'Bearer';
        var obj;
        if (tokenish instanceof Token) {
            return tokenish;
        }
        else if (typeof tokenish === 'string') {
            obj = parse(tokenish);
        }
        else if (typeof tokenish === 'object' && tokenish) {
            obj = tokenish;
        }
        else {
            obj = {};
        }
        tokenProps.forEach(function (prop) {
            if (prop in obj) {
                _this[prop] = obj[prop];
            }
        });
        ['refresh', 'refreshPromise', 'refreshTimeout'].forEach(function (prop) {
            Object.defineProperty(_this, prop, {
                configurable: false,
                enumerable: false,
                writable: true,
            });
        });
    }
    Object.defineProperty(Token.prototype, "expires_in", {
        set: function (value) {
            if (value) {
                this.expires = Date.now() + (1000 * Number(value));
                this.issued = Date.now();
            }
            else {
                this.expires = undefined;
            }
        },
        enumerable: true,
        configurable: true
    });
    Token.prototype.isValid = function (buffer) {
        if (buffer === void 0) { buffer = 0; }
        return !!this.access_token && Number(this.expires) > Date.now() + buffer;
    };
    Token.prototype.logout = function () {
        clearTimeout(this.refreshTimeout);
        this.refresh = undefined;
        this.refreshPromise = undefined;
        this.refreshTimeout = undefined;
    };
    Token.prototype.startRefreshTimer = function (refreshTimestamp) {
        var _this = this;
        clearTimeout(this.refreshTimeout);
        if (typeof refreshTimestamp === 'function') {
            this.refresh = refreshTimestamp(this);
        }
        else {
            this.refresh = this.expires;
        }
        var delay = Math.max(0, this.refresh - Date.now());
        this.refreshPromise = new Promise(function (resolve) {
            _this.refreshTimeout = setTimeout(function () { return resolve(_this); }, delay);
        });
        return this.refreshPromise;
    };
    Token.prototype.toString = function () {
        return stringify(this);
    };
    return Token;
}());

var defaultOptions = {
    client_id: '',
    enableTokenRefresh: false,
    onLoginRedirect: redirect,
    onLogoutRedirect: redirect,
    redirect_url: '',
    url: '',
};
function clearRedirectResponse() {
    if (window.history && window.history.replaceState) {
        var url = window.location.href
            .replace(window.location.hash, '');
        window.history.replaceState(null, '', url);
    }
    else {
        window.location.hash = '';
    }
}
var Auth$1 = (function () {
    function Auth(options) {
        assert(options, ['client_id', 'url', 'redirect_url']);
        this.options = __assign({}, defaultOptions, options);
    }
    Auth.prototype.init = function (tokenish) {
        var token = this.getRedirectResponse();
        if (token) {
            return token;
        }
        token = tokenish && new Token(tokenish);
        if (token && token.isValid()) {
            return this.bindToken(token);
        }
        return undefined;
    };
    Auth.prototype.getRedirectResponse = function (hash) {
        if (hash === void 0) { hash = window.location.hash; }
        if (hash) {
            var token = new Token(hash);
            if (token.isValid()) {
                clearRedirectResponse();
                return this.bindToken(token);
            }
        }
        return undefined;
    };
    Auth.prototype.login = function (options) {
        if (options === void 0) { options = {}; }
        return login(__assign({}, this.options, options));
    };
    Auth.prototype.loginWithSSO = function (options) {
        if (options === void 0) { options = {}; }
        return loginWithSSO(__assign({}, this.options, options));
    };
    Auth.prototype.logout = function (options) {
        if (options === void 0) { options = {}; }
        return logout(__assign({}, this.options, options));
    };
    Auth.prototype.bindToken = function (token) {
        var _this = this;
        if (this.options.enableTokenRefresh) {
            token.startRefreshTimer(this.options.tokenRefreshTimestamp)
                .then(this.options.onTokenRefresh)
                .then(function () { return _this.login(); });
        }
        this.bindLogoutToToken(token);
        return token;
    };
    Auth.prototype.bindLogoutToToken = function (token) {
        var _this = this;
        var tokenLogout = token.logout;
        Object.defineProperty(token, 'logout', {
            enumerable: false,
            value: function () {
                tokenLogout.call(token);
                _this.logout();
            },
        });
    };
    return Auth;
}());

exports['default'] = Auth$1;
exports.Token = Token;

Object.defineProperty(exports, '__esModule', { value: true });

})));

